import { AuthenticatedPhotoDirective } from '@shared/components/entity-photos/authenticated-photo.directive';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subject, catchError, forkJoin, of, switchMap, takeUntil } from 'rxjs';
import { PersonAgeStatus } from '@core/api/generated/api-client';
import { personAgeStatusLabels } from '@core/api/enums/person-age-status.labels';
import { OrganizationLevel, OrganizationUnit, MinistryPersonGroupDto, MemberRow } from '../models/unit.model';
import { UnitService } from '../services/unit.service';

interface MinistryBearer {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  age: number | null;
  ageStatus?: PersonAgeStatus;
  photo?: string;
  ministryName?: string;
  assistantAssignmentName?: string;
  activityUnits: { id?: string; name: string }[];
  ministryStatus?: string;
}

interface MinistryBearerGroup {
  ministryId: number;
  ministryName: string;
  sortOrder: number;
  people: MinistryBearer[];
  isCollapsed: boolean;
  sortBy: 'name' | 'surname' | 'unit';
  statusFilter: 'all' | 'Active' | 'Licensed';
}

@Component({
  selector: 'app-unit-ministry-bearers-page',
  standalone: true,
  imports: [AuthenticatedPhotoDirective, CommonModule, RouterLink, MatCardModule, MatButtonModule, MatIconModule, MatMenuModule, MatProgressSpinnerModule],
  template: `
    <div class="ministry-bearers-page">
      <div *ngIf="isLoading" class="loading-state" aria-label="Cargando portadores de ministerio">
        <mat-spinner diameter="36"></mat-spinner>
      </div>

      <div *ngIf="!isLoading && errorMessage" class="message-state" role="alert">{{ errorMessage }}</div>

      <ng-container *ngIf="!isLoading && !errorMessage">
        <mat-card class="bearer-card leader-card">
          <mat-card-content *ngIf="leader as unitLeader; else emptyLeaderState">
            <div class="leader-avatar avatar">
              <img *ngIf="unitLeader.photo" [appAuthenticatedPhoto]="unitLeader.photo" [alt]="unitLeader.fullName" />
              <span>{{ getInitials(unitLeader.firstName, unitLeader.lastName) }}</span>
            </div>
            <div class="leader-info">
              <div class="eyebrow">{{ leaderAppointmentName }}</div>
              <a class="person-name leader-name" [routerLink]="['/people', unitLeader.id]">{{ unitLeader.fullName }}</a>
              <div class="person-meta">
                <span class="leader-ministry">{{ leaderMinistry || 'Ministerio no informado' }}</span>
                <span class="leader-age" *ngIf="unitLeader.age !== null">{{ unitLeader.age }} años</span>
                <span class="leader-age-category" *ngIf="unitLeader.ageStatus">{{ getAgeStatusLabel(unitLeader.ageStatus) }}</span>
              </div>
            </div>
          </mat-card-content>
          <ng-template #emptyLeaderState>
            <div class="leader-empty-state">
              <div class="leader-avatar avatar">
                <svg class="leader-person-icon" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </svg>
              </div>
              <div class="leader-info">
                <div class="eyebrow">{{ leaderAppointmentName }}</div>
                <div class="leader-empty-message">La unidad no tiene un dirigente asignado</div>
              </div>
            </div>
          </ng-template>
        </mat-card>

        <div class="ministry-columns" *ngIf="ministryGroups.length > 0">
          <div class="ministry-column" *ngFor="let groups of ministryColumns">
            <mat-card class="bearer-card ministry-card" *ngFor="let group of groups" [class.ministry-card--collapsed]="group.isCollapsed">
              <mat-card-header class="ministry-card-header">
                <div class="ministry-heading" role="button" tabindex="0"
                  [attr.aria-expanded]="!group.isCollapsed"
                  (click)="toggleGroup(group)"
                  (keydown.enter)="toggleGroup(group)"
                  (keydown.space)="$event.preventDefault(); toggleGroup(group)">
                  <mat-icon class="collapse-icon">{{ group.isCollapsed ? 'chevron_right' : 'expand_more' }}</mat-icon>
                  <mat-card-title>{{ group.ministryName }}</mat-card-title>
                  <span class="people-count people-count--active">{{ getMinistryStatusCount(group, 'Active') }} activos</span>
                  <span *ngIf="getMinistryStatusCount(group, 'Licensed') > 0" class="people-count people-count--licensed">{{ getMinistryStatusCount(group, 'Licensed') }} de licencia</span>
                </div>
                <div class="card-controls" (click)="$event.stopPropagation()">
                  <button mat-icon-button type="button" aria-label="Ordenar portadores" [matMenuTriggerFor]="sortMenu">
                    <mat-icon>sort_by_alpha</mat-icon>
                  </button>
                  <mat-menu #sortMenu="matMenu">
                    <button mat-menu-item type="button" (click)="setSort(group, 'name')">Por nombre</button>
                    <button mat-menu-item type="button" (click)="setSort(group, 'surname')">Por apellido</button>
                    <button mat-menu-item type="button" (click)="setSort(group, 'unit')">Por unidad donde está activo</button>
                  </mat-menu>
                  <button mat-icon-button type="button" aria-label="Filtrar portadores" [matMenuTriggerFor]="filterMenu">
                    <mat-icon>filter_list</mat-icon>
                  </button>
                  <mat-menu #filterMenu="matMenu">
                    <button mat-menu-item type="button" (click)="setStatusFilter(group, 'all')">Todos</button>
                    <button mat-menu-item type="button" (click)="setStatusFilter(group, 'Active')">Activos</button>
                    <button mat-menu-item type="button" (click)="setStatusFilter(group, 'Licensed')">De licencia</button>
                  </mat-menu>
                </div>
              </mat-card-header>
              <mat-card-content *ngIf="!group.isCollapsed">
                <div class="bearer-row" *ngFor="let person of getVisiblePeople(group)">
                  <div class="small-avatar avatar">
                    <img *ngIf="person.photo" [appAuthenticatedPhoto]="person.photo" [alt]="person.fullName" />
                    <span>{{ getInitials(person.firstName, person.lastName) }}</span>
                  </div>
                  <div class="bearer-info">
                    <a class="person-name" [routerLink]="['/people', person.id]">{{ person.fullName }}</a>
                    <span *ngIf="person.ministryStatus === 'Licensed'" class="licensed-badge">
                      <mat-icon aria-hidden="true">event_busy</mat-icon>De licencia
                    </span>
                    <span *ngIf="person.assistantAssignmentName" class="assistant-badge">
                      <mat-icon aria-hidden="true">verified</mat-icon>{{ person.assistantAssignmentName }}
                    </span>
                    <div class="activity-units" *ngIf="includeActivityUnits && person.activityUnits.length">
                      <span>Activo en:</span>
                      <ng-container *ngFor="let unit of person.activityUnits; let last = last">
                        <a *ngIf="unit.id; else plainActivityUnit" [routerLink]="['/units', unit.id]">{{ unit.name }}</a>
                        <ng-template #plainActivityUnit>{{ unit.name }}</ng-template>
                        <span *ngIf="!last">, </span>
                      </ng-container>
                    </div>
                  </div>
                  <span class="person-age" *ngIf="person.age !== null">{{ person.age }} años</span>
                </div>
                <div *ngIf="getVisiblePeople(group).length === 0" class="empty-filter-state">No hay portadores con este filtro.</div>
              </mat-card-content>
            </mat-card>
          </div>
        </div>

        <div *ngIf="ministryGroups.length === 0" class="empty-state">
          <mat-icon>groups_off</mat-icon>
          <span>No hay portadores de ministerio activos en esta unidad.</span>
        </div>
      </ng-container>
    </div>
  `,
  styles: [`
    :host { display: block; height: 100%; min-height: 0; }
    .ministry-bearers-page { box-sizing: border-box; display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); grid-auto-rows: max-content; align-content: start; gap: 16px; height: 100%; min-height: 0; overflow: auto; padding: 16px; }
    .ministry-columns { display: grid; grid-column: 1 / -1; grid-template-columns: repeat(2, minmax(0, 1fr)); align-items: start; gap: 16px; min-width: 0; }
    .ministry-column { display: flex; flex-direction: column; gap: 16px; min-width: 0; }
    .bearer-card { box-sizing: border-box; overflow: hidden; border: 1px solid #dfe3e8; border-radius: 12px; box-shadow: none; }
    .leader-card { grid-column: 1 / -1; width: 100%; }
    .leader-card mat-card-content { display: flex; align-items: center; gap: 20px; padding: 22px 24px; }
    .avatar { position: relative; display: flex; flex: 0 0 auto; align-items: center; justify-content: center; overflow: hidden; border-radius: 50%; color: #fff; background: #5b91c6; font-weight: 600; }
    .avatar img { position: absolute; z-index: 1; inset: 0; width: 100%; height: 100%; object-fit: cover; }
    .leader-avatar { width: 76px; height: 76px; font-size: 25px; }
    .leader-person-icon { width: 44px; height: 44px; fill: #fff; }
    .leader-info { display: flex; flex-direction: column; gap: 5px; min-width: 0; }
    .eyebrow { color: #607080; font-size: 12px; font-weight: 600; letter-spacing: .04em; text-transform: uppercase; }
    .person-name { color: #173f68; font-weight: 600; text-decoration: none; }
    .person-name:hover { color: #1675e8; text-decoration: underline; }
    .leader-name { font-size: 20px; }
    .leader-empty-message { color: #173f68; font-size: 20px; font-weight: 600; }
    .person-meta { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
    .leader-ministry { color: #315f8a; font-size: 15px; font-weight: 600; }
    .leader-age { color: #607080; font-size: 14px; }
    .leader-age-category { padding: 2px 8px; border-radius: 999px; color: #526477; background: #edf2f6; font-size: 12px; font-weight: 500; }
    .ministry-card { display: flex; max-height: 420px; min-height: 0; flex-direction: column; }
    .ministry-card--collapsed { height: auto !important; min-height: 0 !important; }
    .ministry-card mat-card-header { display: flex; flex: 0 0 auto; align-items: center; justify-content: flex-start; gap: 12px; padding: 12px 16px 10px 20px; }
    .ministry-heading { display: flex; flex: 1 1 auto; min-width: 0; flex-wrap: wrap; align-items: center; justify-content: flex-start; gap: 12px; cursor: pointer; }
    .ministry-heading:focus-visible { outline: 2px solid #1675e8; outline-offset: 2px; }
    .card-controls { display: flex; flex: 0 0 auto; align-items: center; }
    .card-controls button { width: 36px; height: 36px; color: #52718e; }
    .collapse-icon { color: #5b91c6; }
    .ministry-card mat-card-title { flex: 0 0 auto; margin: 0; color: #173f68; font-size: 17px; font-weight: 600; text-align: left; }
    .people-count { display: inline-flex; min-width: 26px; height: 26px; align-items: center; justify-content: center; padding: 0 7px; border-radius: 999px; color: #315c80; background: #e8f2fa; font-size: 12px; font-weight: 600; }
    .people-count--licensed { color: #80520b; background: #fff2d6; }
    .ministry-card mat-card-content { flex: 0 1 auto; max-height: 340px; min-height: 0; overflow-y: auto; padding: 0 20px 8px; }
    .bearer-row { display: flex; min-height: 64px; align-items: center; gap: 14px; border-top: 1px solid #edf0f3; }
    .small-avatar { width: 38px; height: 38px; font-size: 13px; }
    .bearer-info { display: flex; flex: 1 1 auto; flex-direction: column; gap: 4px; min-width: 0; }
    .assistant-badge { display: inline-flex; width: fit-content; align-items: center; gap: 4px; padding: 2px 8px; border-radius: 999px; color: #315f8a; background: #e8f2fa; font-size: 11px; font-weight: 600; }
    .assistant-badge mat-icon { width: 14px; height: 14px; font-size: 14px; }
    .licensed-badge { display: inline-flex; width: fit-content; align-items: center; gap: 4px; padding: 2px 8px; border-radius: 999px; color: #80520b; background: #fff2d6; font-size: 11px; font-weight: 600; }
    .licensed-badge mat-icon { width: 14px; height: 14px; font-size: 14px; }
    .activity-units { display: flex; flex-wrap: wrap; gap: 0 4px; color: #607080; font-size: 12px; }
    .activity-units a { color: #1675e8; text-decoration: none; }
    .activity-units a:hover { text-decoration: underline; }
    .person-age { flex: 0 0 auto; color: #607080; font-size: 13px; }
    .empty-filter-state { padding: 18px 0; color: #607080; font-size: 13px; text-align: center; }
    .loading-state, .message-state, .empty-state, .leader-empty-state { display: flex; align-items: center; justify-content: center; gap: 10px; color: #607080; }
    .loading-state, .message-state { min-height: 120px; }
    .empty-state { grid-column: 1 / -1; min-height: 140px; flex-direction: column; border: 1px dashed #d5dfe8; border-radius: 12px; }
    .empty-state mat-icon, .leader-empty-state > mat-icon { color: #7196b7; }
    .leader-empty-state { display: flex; min-height: 120px; align-items: center; justify-content: flex-start; gap: 20px; padding: 22px 24px; }
    @media (max-width: 600px) {
      .ministry-bearers-page { grid-template-columns: minmax(0, 1fr); padding: 12px; }
      .ministry-columns { grid-template-columns: minmax(0, 1fr); }
      .leader-card mat-card-content { gap: 14px; padding: 18px; }
      .leader-avatar { width: 60px; height: 60px; font-size: 20px; }
      .leader-name { font-size: 17px; }
      .bearer-row { flex-wrap: wrap; gap: 8px 12px; padding: 10px 0; }
      .bearer-info { flex-basis: calc(100% - 58px); }
      .person-age { margin-left: 50px; }
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UnitMinistryBearersPageComponent implements OnInit, OnDestroy {
  isLoading = true;
  errorMessage: string | null = null;
  leader: MinistryBearer | null = null;
  leaderMinistry = '';
  leaderAppointmentName = '';
  private assistantAssignmentNameByPersonId = new Map<string, string>();
  ministryGroups: MinistryBearerGroup[] = [];
  ministryColumns: MinistryBearerGroup[][] = [[], []];
  includeActivityUnits = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly unitService: UnitService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    const unitId = this.route.pathFromRoot
      .map(route => route.snapshot.paramMap.get('unit-id'))
      .find((value): value is string => !!value);

    if (!unitId) {
      this.isLoading = false;
      this.errorMessage = 'No se pudo abrir la unidad solicitada.';
      return;
    }

    this.route.paramMap.pipe(
      switchMap(() => forkJoin({
        unit: this.unitService.getUnit(unitId),
        groups: this.unitService.getMinistries(unitId),
        members: this.unitService.getMembersFull(unitId).pipe(catchError(() => of([] as MemberRow[]))),
      })),
      catchError(() => {
        this.errorMessage = 'No se pudieron cargar los portadores de ministerio.';
        return of(null);
      }),
      takeUntil(this.destroy$),
    ).subscribe(result => {
      if (!result) {
        this.isLoading = false;
        this.cdr.markForCheck();
        return;
      }

      const membersById = new Map(result.members.map(member => [member.id.toLowerCase(), member]));
      const leaderId = result.unit.leaderId?.toLowerCase();
      const assistantAssignmentName = this.getAssistantAssignmentName(result.unit.organizationLevelId);
      this.assistantAssignmentNameByPersonId = new Map(result.members.flatMap(member => {
        const assignmentName = member.activeAssignments?.find(assignment =>
          assistantAssignmentName && assignment.assignmentName.toLocaleLowerCase('es') === assistantAssignmentName.toLocaleLowerCase('es'))?.assignmentName;
        return assignmentName ? [[member.id.toLowerCase(), assignmentName] as const] : [];
      }));
      this.includeActivityUnits = result.unit.organizationLevelId !== OrganizationLevel.Comunidad;
      this.ministryGroups = result.groups
        .map(group => ({
          ministryId: group.ministryId,
          ministryName: group.ministryName,
          sortOrder: group.sortOrder,
          isCollapsed: false,
          sortBy: 'surname' as const,
          statusFilter: 'all' as const,
          people: [...group.people
            .filter(person => (person.status === 'Active' || person.status === 'Licensed') && person.personId.toLowerCase() !== leaderId)
            .reduce((peopleById, person) => {
              const personId = person.personId.toLowerCase();
              const bearer = peopleById.get(personId) ?? {
                ...this.toBearer(person.personId, person.firstName, person.lastName, membersById.get(personId)),
                assistantAssignmentName: this.assistantAssignmentNameByPersonId.get(personId),
                ministryStatus: person.status,
              };
              const unitName = person.unitName?.trim();
              if (this.includeActivityUnits && unitName && !bearer.activityUnits.some(unit => unit.id === person.unitId || (!unit.id && unit.name === unitName))) {
                bearer.activityUnits.push({ id: person.unitId, name: unitName });
              }
              peopleById.set(personId, bearer);
              return peopleById;
            }, new Map<string, MinistryBearer>()).values()]
            .sort((a, b) => a.lastName.localeCompare(b.lastName, 'es', { sensitivity: 'base' }) || a.firstName.localeCompare(b.firstName, 'es', { sensitivity: 'base' })),
        }))
        .filter(group => group.people.length > 0)
        .sort((a, b) => b.sortOrder - a.sortOrder);
      const firstColumnLength = Math.ceil(this.ministryGroups.length / 2);
      this.ministryColumns = [
        this.ministryGroups.slice(0, firstColumnLength),
        this.ministryGroups.slice(firstColumnLength),
      ];
      const leaderMember = leaderId ? membersById.get(leaderId) : undefined;
      const leaderBearer = result.unit.leaderId
        ? this.findBearer(result.groups, result.unit.leaderId, membersById)
        : null;
      if (leaderBearer || result.unit.leaderId || result.unit.leader) {
        const fullName = leaderBearer?.fullName || result.unit.leader || '';
        const [firstName = '', ...lastNameParts] = fullName.split(/\s+/);
        this.leader = leaderBearer ?? this.toBearer(result.unit.leaderId ?? '', firstName, lastNameParts.join(' '), leaderMember);
      } else {
        this.leader = null;
      }
      this.leaderMinistry = result.unit.leaderMinistryName || leaderBearer?.ministryName || '';
      this.leaderAppointmentName = result.unit.leaderAppointmentName || this.getLeaderAppointmentFallback(result.unit.organizationLevelId);
      this.isLoading = false;
      this.cdr.markForCheck();
    });
  }

  getInitials(firstName: string, lastName: string): string {
    return `${firstName.trim().charAt(0)}${lastName.trim().charAt(0)}`.toLocaleUpperCase('es');
  }

  getAgeStatusLabel(status: PersonAgeStatus): string {
    return personAgeStatusLabels[status] ?? status;
  }

  getMinistryStatusCount(group: MinistryBearerGroup, status: 'Active' | 'Licensed'): number {
    return group.people.filter(person => person.ministryStatus === status).length;
  }

  getVisiblePeople(group: MinistryBearerGroup): MinistryBearer[] {
    return group.people
      .filter(person => group.statusFilter === 'all' || person.ministryStatus === group.statusFilter)
      .sort((a, b) => {
        if (group.sortBy === 'name') {
          return a.firstName.localeCompare(b.firstName, 'es', { sensitivity: 'base' }) || a.lastName.localeCompare(b.lastName, 'es', { sensitivity: 'base' });
        }
        if (group.sortBy === 'unit') {
          const aUnit = a.activityUnits.map(unit => unit.name).sort((x, y) => x.localeCompare(y, 'es', { sensitivity: 'base' }))[0] ?? '';
          const bUnit = b.activityUnits.map(unit => unit.name).sort((x, y) => x.localeCompare(y, 'es', { sensitivity: 'base' }))[0] ?? '';
          return aUnit.localeCompare(bUnit, 'es', { sensitivity: 'base' }) || a.lastName.localeCompare(b.lastName, 'es', { sensitivity: 'base' });
        }
        return a.lastName.localeCompare(b.lastName, 'es', { sensitivity: 'base' }) || a.firstName.localeCompare(b.firstName, 'es', { sensitivity: 'base' });
      });
  }

  setSort(group: MinistryBearerGroup, sortBy: MinistryBearerGroup['sortBy']): void {
    group.sortBy = sortBy;
    this.cdr.markForCheck();
  }

  setStatusFilter(group: MinistryBearerGroup, statusFilter: MinistryBearerGroup['statusFilter']): void {
    group.statusFilter = statusFilter;
    this.cdr.markForCheck();
  }

  private getLeaderAppointmentFallback(levelId: number): string {
    switch (levelId) {
      case OrganizationLevel.IglesiaRegional: return 'Apóstol de Distrito';
      case OrganizationLevel.AreaApostol: return 'Dirigente de Área Apóstol';
      case OrganizationLevel.Distrito: return 'Dirigente de Distrito';
      case OrganizationLevel.GrupoComunidad: return 'Dirigente de Grupo de Comunidad';
      case OrganizationLevel.Comunidad: return 'Dirigente de Comunidad';
      default: return 'Dirigente de la unidad';
    }
  }

  private getAssistantAssignmentName(levelId: number): string | null {
    switch (levelId) {
      case OrganizationLevel.Comunidad: return 'Ayudante del dirigente de Comunidad';
      case OrganizationLevel.Distrito: return 'Ayudante del dirigente de Distrito';
      case OrganizationLevel.AreaApostol: return 'Obispo';
      case OrganizationLevel.IglesiaRegional: return 'Ayudante del Apóstol de Distrito';
      default: return null;
    }
  }

  toggleGroup(group: MinistryBearerGroup): void {
    group.isCollapsed = !group.isCollapsed;
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private findBearer(groups: MinistryPersonGroupDto[], personId: string, membersById: Map<string, MemberRow>): MinistryBearer | null {
    for (const group of groups) {
      const person = group.people.find(candidate => candidate.personId.toLowerCase() === personId.toLowerCase() && candidate.status === 'Active');
      if (person) {
        return { ...this.toBearer(person.personId, person.firstName, person.lastName, membersById.get(person.personId.toLowerCase())), ministryName: group.ministryName };
      }
    }
    return null;
  }

  private toBearer(id: string, firstName: string, lastName: string, member?: MemberRow): MinistryBearer {
    const optionalMember = member as (MemberRow & { photo?: string; photoUrl?: string }) | undefined;
    return {
      id,
      firstName,
      lastName,
      fullName: `${firstName} ${lastName}`.trim(),
      age: member?.age ?? null,
      ageStatus: member?.ageStatus,
      photo: optionalMember?.photo ?? optionalMember?.photoUrl,
      activityUnits: [],
    };
  }
}
